import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  useCancelVanOrder,
  useCreateVanOrder,
  usePackVanOrder,
  usePickUpVanOrder,
  useReorderVanOrderRest,
  useScanStock,
  useStockItems,
  useVanOrder,
  useVanOrderContext,
  useVanOrders,
  useVanOrderSuggestion
} from '../api/queries'
import type { StockItem, VanOrder, VanOrderStatus } from '../api/types'
import { CodeScanner } from '../components/CodeScanner'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDateTime } from '../lib/format'
import { normalizeScanned } from '../lib/scanner'

/**
 * Varer til bilen (beslutning 127): montøren bestiller, lageret pakker, montøren kvitterer.
 *
 * <b>Intet flyttes, før montøren har trykket "Hentet".</b> Så flytter serveren det pakkede fra lageret til bilen -
 * kun kartotekets varer; fri tekst er bare en besked til lageret.
 */

const STATUS: Record<VanOrderStatus, { label: string; color: string }> = {
  sent: { label: 'Venter på at blive pakket', color: '#EAB308' },
  packed: { label: 'Klar til afhentning', color: '#22C55E' },
  picked_up: { label: 'Hentet', color: '#64748B' },
  cancelled: { label: 'Annulleret', color: '#94A3B8' }
}

function StatusChip({ status }: { status: VanOrderStatus }) {
  const s = STATUS[status]
  return (
    <span className="tiny" style={{ color: s.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
      ● {s.label}
    </span>
  )
}

/** "12,5 m" / "3 stk" - antallet med dansk komma. */
function qty(value: number, unit?: string | null) {
  const text = value.toLocaleString('da-DK', { maximumFractionDigits: 3 })
  return unit ? `${text} ${unit}` : text
}

function parseQty(text: string) {
  const value = Number(text.replace(',', '.'))
  return Number.isFinite(value) ? value : NaN
}

// ---------------------------------------------------------------------------
// Listen og en ny bestilling
// ---------------------------------------------------------------------------

export function VanOrdersPage() {
  const context = useVanOrderContext()
  const orders = useVanOrders()
  // "+" -> "Varer til bilen" aabner formularen med det samme.
  const [params, setParams] = useSearchParams()
  const [creating, setCreating] = useState(params.get('ny') === '1')

  useEffect(() => {
    if (params.get('ny') === '1') {
      setCreating(true)
      setParams({}, { replace: true })
    }
  }, [params, setParams])

  if (context.isLoading || orders.isLoading) return <Loading />
  if (context.error) return <ErrorBox error={context.error} />
  if (orders.error) return <ErrorBox error={orders.error} />

  const ctx = context.data!
  const list = orders.data ?? []
  const open = list.filter((o) => o.status === 'sent' || o.status === 'packed')
  const done = list.filter((o) => o.status === 'picked_up' || o.status === 'cancelled')
  const vans = ctx.canPack ? ctx.allVans : ctx.myVans

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Varer til bilen
        </h1>
        {!creating && vans.length > 0 && (
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            + Bestil varer
          </button>
        )}
      </div>

      {vans.length === 0 && (
        <div className="map-notice">
          Du står ikke på en bil. Få en administrator til at sætte dig som ansvarlig på bilen under Lagre og biler -
          eller som fører af firmabilen.
        </div>
      )}

      {creating && vans.length > 0 && (
        <NewOrderForm
          vans={vans}
          defaultVanId={ctx.myVans[0]?.id ?? vans[0]?.id}
          onDone={() => setCreating(false)}
        />
      )}

      <Card title={ctx.canPack ? `Åbne bestillinger (${open.length})` : 'Mine bestillinger'}>
        {ctx.canPack && !ctx.hasPackingGroup && (
          <div className="tiny muted" style={{ marginBottom: 10 }}>
            Ingen arbejdsgruppe pakker endnu, så det er kontoret. Sæt fluebenet "Pakker varer til bilerne" på en
            gruppe under Intranet → Arbejdsgrupper.
          </div>
        )}
        {open.length === 0 ? <EmptyState>Ingen åbne bestillinger.</EmptyState> : <OrderList orders={open} />}
      </Card>

      {done.length > 0 && (
        <Card title="Hentet og annulleret">
          <OrderList orders={done} />
        </Card>
      )}
    </div>
  )
}

function OrderList({ orders }: { orders: VanOrder[] }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {orders.map((o) => (
        <Link
          key={o.id}
          to={`/stock/orders/${o.id}`}
          className="row row--between"
          style={{ gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border, #E2E8F0)' }}
        >
          <div style={{ minWidth: 0 }}>
            <div className="small">
              <strong>🚐 {o.van.name}</strong> · {o.lines.length} {o.lines.length === 1 ? 'vare' : 'varer'}
            </div>
            <div className="tiny muted">
              #{o.id} · {o.orderedBy.displayName} · {formatDateTime(o.createdAt)}
            </div>
          </div>
          <StatusChip status={o.status} />
        </Link>
      ))}
    </div>
  )
}

/** En linje i den nye bestilling: en vare fra kartoteket eller fri tekst. */
interface DraftLine {
  key: string
  itemId?: number
  name: string
  unit?: string
  quantity: string
}

function NewOrderForm({
  vans,
  defaultVanId,
  onDone
}: {
  vans: { id: number; name: string }[]
  defaultVanId?: number
  onDone: () => void
}) {
  const navigate = useNavigate()
  const create = useCreateVanOrder()
  const suggest = useVanOrderSuggestion()
  const scan = useScanStock()

  const [vanId, setVanId] = useState(defaultVanId ?? 0)
  const [lines, setLines] = useState<DraftLine[]>([])
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [freeText, setFreeText] = useState('')
  const [note, setNote] = useState('')
  const [scanning, setScanning] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const items = useStockItems(debounced.length >= 2 ? { search: debounced } : undefined)
  const results = useMemo(() => (debounced.length >= 2 ? (items.data ?? []).slice(0, 8) : []), [debounced, items.data])

  function addItem(item: Pick<StockItem, 'id' | 'name' | 'unit'>, quantity = 1) {
    setLines((current) => {
      const existing = current.find((l) => l.itemId === item.id)
      if (existing) {
        // Samme vare igen = en mere, ligesom naar der scannes (beslutning 071).
        return current.map((l) =>
          l === existing ? { ...l, quantity: String((parseQty(l.quantity) || 0) + quantity) } : l
        )
      }
      return [...current, { key: `i${item.id}`, itemId: item.id, name: item.name, unit: item.unit, quantity: String(quantity) }]
    })
  }

  async function fillUp() {
    setMessage(null)
    try {
      const suggestions = await suggest.mutateAsync(vanId)
      if (suggestions.length === 0) {
        setMessage('Bilen mangler ikke noget - alt er over minimum, eller også er det allerede bestilt.')
        return
      }
      suggestions.forEach((s) => addItem({ id: s.itemId, name: s.name, unit: s.unit }, s.quantity))
      setMessage(`${suggestions.length} ${suggestions.length === 1 ? 'vare' : 'varer'} under minimum er lagt i bestillingen.`)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Forslaget kunne ikke hentes.')
    }
  }

  async function handleCode(code: string) {
    setMessage(null)
    try {
      const result = await scan.mutateAsync(normalizeScanned(code))
      if (result.kind === 'item' && result.item) {
        addItem(result.item)
        setMessage(`${result.item.name} er lagt i bestillingen.`)
      } else {
        setMessage(result.message ?? 'Koden er ikke i kartoteket - skriv varen som fri tekst.')
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Koden kunne ikke slås op.')
    }
  }

  function addFreeText() {
    const text = freeText.trim()
    if (!text) return
    setLines((current) => [...current, { key: `t${Date.now()}`, name: text, quantity: '1' }])
    setFreeText('')
  }

  async function submit() {
    setError(null)
    if (lines.some((l) => !(parseQty(l.quantity) > 0))) {
      setError('Alle antal skal være større end nul.')
      return
    }
    try {
      const order = await create.mutateAsync({
        vanLocationId: vanId,
        note: note.trim() || null,
        lines: lines.map((l) =>
          l.itemId ? { itemId: l.itemId, quantity: parseQty(l.quantity) } : { text: l.name, quantity: parseQty(l.quantity) }
        )
      })
      onDone()
      navigate(`/stock/orders/${order.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bestillingen kunne ikke sendes.')
    }
  }

  return (
    <Card
      title="Ny bestilling"
      actions={
        <button className="btn btn--sm" onClick={onDone}>
          Fortryd
        </button>
      }
    >
      <div className="stack" style={{ gap: 12 }}>
        <label className="small">
          Til bilen
          <select value={vanId} onChange={(e) => setVanId(Number(e.target.value))}>
            {vans.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn--sm" disabled={!vanId || suggest.isPending} onClick={() => void fillUp()}>
            {suggest.isPending ? 'Henter ...' : '🔄 Fyld op'}
          </button>
          <button className="btn btn--sm" onClick={() => setScanning((s) => !s)}>
            {scanning ? 'Luk scanneren' : '📷 Scan'}
          </button>
        </div>
        <div className="tiny muted">
          "Fyld op" lægger alt, der er under bilens minimum, i bestillingen - minus det, der allerede er bestilt.
        </div>

        {scanning && (
          <CodeScanner onCode={(code) => void handleCode(code)} busy={scan.isPending} hint="Scan en vare ad gangen" />
        )}

        <label className="small">
          Søg i kartoteket
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Navn, varenummer eller kode" />
        </label>
        {results.length > 0 && (
          <div className="stack" style={{ gap: 4 }}>
            {results.map((item) => (
              <button
                key={item.id}
                className="btn btn--sm"
                style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                onClick={() => {
                  addItem(item)
                  setSearch('')
                }}
              >
                + {item.name} <span className="tiny muted">({item.code})</span>
              </button>
            ))}
          </div>
        )}
        {debounced.length >= 2 && !items.isLoading && results.length === 0 && (
          <div className="tiny muted">Ingen varer fundet - skriv den som fri tekst nedenfor.</div>
        )}

        <div className="row" style={{ gap: 8 }}>
          <input
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addFreeText()
              }
            }}
            placeholder="Fri tekst - det, der ikke står i kartoteket"
            maxLength={200}
          />
          <button className="btn btn--sm" disabled={!freeText.trim()} onClick={addFreeText}>
            Tilføj
          </button>
        </div>

        {message && <div className="map-notice">{message}</div>}

        {lines.length === 0 ? (
          <EmptyState>Ingen varer endnu.</EmptyState>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {lines.map((line) => (
              <div key={line.key} className="row" style={{ gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }} className="small">
                  {line.name}
                  {!line.itemId && <span className="tiny muted"> · fri tekst</span>}
                </div>
                <input
                  style={{ width: 80 }}
                  inputMode="decimal"
                  value={line.quantity}
                  onChange={(e) =>
                    setLines((current) => current.map((l) => (l.key === line.key ? { ...l, quantity: e.target.value } : l)))
                  }
                  aria-label={`Antal ${line.name}`}
                />
                <span className="tiny muted" style={{ width: 28 }}>
                  {line.unit ?? ''}
                </span>
                <button
                  className="btn btn--sm"
                  onClick={() => setLines((current) => current.filter((l) => l.key !== line.key))}
                  aria-label={`Fjern ${line.name}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="small">
          Besked til lageret
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="F.eks. hentes i morgen kl. 7" maxLength={500} />
        </label>

        {error && <div className="error-box">{error}</div>}

        <button
          className="btn btn--primary"
          disabled={lines.length === 0 || !vanId || create.isPending}
          onClick={() => void submit()}
        >
          {create.isPending ? 'Sender ...' : `Send bestillingen (${lines.length})`}
        </button>
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Én bestilling: pak, hent, annullér, bestil resten
// ---------------------------------------------------------------------------

export function VanOrderDetailPage() {
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const { data: order, isLoading, error } = useVanOrder(id)
  const context = useVanOrderContext()
  const pickUp = usePickUpVanOrder()
  const cancel = useCancelVanOrder()
  const reorder = useReorderVanOrderRest()
  const [actionError, setActionError] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!order) return null

  async function run(action: () => Promise<VanOrder>, goTo?: (o: VanOrder) => string) {
    setActionError(null)
    try {
      const result = await action()
      if (goTo) navigate(goTo(result))
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Det lykkedes ikke.')
    }
  }

  const packed = order.status === 'packed' || order.status === 'picked_up'

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          🚐 {order.van.name} <span className="muted">#{order.id}</span>
        </h1>
        <Link className="btn btn--sm" to="/stock/orders">
          Alle bestillinger
        </Link>
      </div>

      <Card>
        <div className="stack" style={{ gap: 6 }}>
          <StatusChip status={order.status} />
          <div className="small">
            Bestilt af <strong>{order.orderedBy.displayName}</strong> {formatDateTime(order.createdAt)}
          </div>
          {order.note && <div className="small">💬 {order.note}</div>}
          {order.packedAt && (
            <div className="small">
              Pakket af {order.packedBy?.displayName ?? '-'} {formatDateTime(order.packedAt)}
              {order.from && <> fra <strong>{order.from.name}</strong></>}
            </div>
          )}
          {order.packNote && <div className="small">📦 {order.packNote}</div>}
          {order.pickedUpAt && (
            <div className="small">
              Hentet af {order.pickedUpBy?.displayName ?? '-'} {formatDateTime(order.pickedUpAt)}
            </div>
          )}
          {order.cancelledAt && <div className="small">Annulleret {formatDateTime(order.cancelledAt)}</div>}
        </div>
      </Card>

      {order.canPack && context.data ? (
        <PackForm order={order} packFrom={context.data.packFrom} />
      ) : (
        <Card title="Varerne">
          <div className="stack" style={{ gap: 6 }}>
            {order.lines.map((line) => {
              const rest = packed && (line.packedQuantity ?? 0) < line.quantity
              return (
                <div key={line.id} className="row row--between small" style={{ gap: 10 }}>
                  <span>
                    {line.name}
                    {!line.itemId && <span className="tiny muted"> · fri tekst</span>}
                  </span>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {packed ? (
                      <>
                        {qty(line.packedQuantity ?? 0, line.unit)} af {qty(line.quantity, line.unit)}
                        {rest && <span className="warn"> · rest</span>}
                      </>
                    ) : (
                      qty(line.quantity, line.unit)
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {actionError && <div className="error-box">{actionError}</div>}

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {order.canPickUp && (
          <button
            className="btn btn--primary"
            disabled={pickUp.isPending}
            onClick={() => void run(() => pickUp.mutateAsync(order.id))}
          >
            {pickUp.isPending ? 'Flytter ...' : '✅ Hentet - flyt varerne til bilen'}
          </button>
        )}
        {order.hasRest && (
          <button
            className="btn"
            disabled={reorder.isPending}
            onClick={() => void run(() => reorder.mutateAsync(order.id), (o) => `/stock/orders/${o.id}`)}
          >
            Bestil resten
          </button>
        )}
        {order.canCancel && (
          <button
            className="btn btn--danger"
            disabled={cancel.isPending}
            onClick={() => {
              if (window.confirm('Annullér bestillingen? Intet bliver flyttet.')) {
                void run(() => cancel.mutateAsync(order.id))
              }
            }}
          >
            Annullér
          </button>
        )}
      </div>
      {order.canPickUp && (
        <div className="tiny muted">
          Tryk "Hentet", når du har varerne. Så flyttes kartotekets varer fra {order.from?.name ?? 'lageret'} til{' '}
          {order.van.name}.
        </div>
      )}
    </div>
  )
}

function PackForm({ order, packFrom }: { order: VanOrder; packFrom: { id: number; name: string }[] }) {
  const pack = usePackVanOrder()
  const [fromId, setFromId] = useState(order.from?.id ?? packFrom[0]?.id ?? 0)
  const [packNote, setPackNote] = useState(order.packNote ?? '')
  const [quantities, setQuantities] = useState<Record<number, string>>(() =>
    Object.fromEntries(order.lines.map((l) => [l.id, String(l.packedQuantity ?? l.quantity).replace('.', ',')]))
  )
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    const lines = order.lines.map((l) => ({ lineId: l.id, packedQuantity: parseQty(quantities[l.id] || '0') }))
    if (lines.some((l) => !(l.packedQuantity >= 0))) {
      setError('Skriv et antal på hver linje - 0, hvis den ikke er på lager.')
      return
    }
    try {
      await pack.mutateAsync({ id: order.id, input: { fromLocationId: fromId, packNote: packNote.trim() || null, lines } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bestillingen kunne ikke meldes pakket.')
    }
  }

  return (
    <Card title={order.status === 'packed' ? 'Pakket - kan rettes, til den er hentet' : 'Pak bestillingen'}>
      <div className="stack" style={{ gap: 10 }}>
        <label className="small">
          Pakket fra
          <select value={fromId} onChange={(e) => setFromId(Number(e.target.value))}>
            {packFrom.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <div className="tiny muted">Skriv hvor mange der er pakket. 0 = ikke på lager - så står det som rest.</div>
        {order.lines.map((line) => (
          <div key={line.id} className="row" style={{ gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }} className="small">
              {line.name}
              {!line.itemId && <span className="tiny muted"> · fri tekst</span>}
              <div className="tiny muted">Bestilt: {qty(line.quantity, line.unit)}</div>
            </div>
            <input
              style={{ width: 80 }}
              inputMode="decimal"
              value={quantities[line.id] ?? ''}
              onChange={(e) => setQuantities((q) => ({ ...q, [line.id]: e.target.value }))}
              aria-label={`Pakket ${line.name}`}
            />
            <span className="tiny muted" style={{ width: 28 }}>
              {line.unit ?? ''}
            </span>
          </div>
        ))}

        <label className="small">
          Besked til montøren
          <input value={packNote} onChange={(e) => setPackNote(e.target.value)} placeholder="F.eks. står på hylde 3" maxLength={500} />
        </label>

        {error && <div className="error-box">{error}</div>}

        <button className="btn btn--primary" disabled={!fromId || pack.isPending} onClick={() => void submit()}>
          {pack.isPending ? 'Gemmer ...' : order.status === 'packed' ? 'Gem rettelsen' : '📦 Pakket - giv montøren besked'}
        </button>
      </div>
    </Card>
  )
}
